import { store } from '../store';
import {
  hasUpdateAtom,
  updateVersionAtom,
  isCheckingUpdateAtom,
} from '../store/update/updateState';
import { BUILD_TIME_PATH, probeAppHost } from '../store/network/networkState';

// 'reloading': the app is about to reload into the new version. 'unreachable': the
// app host did not answer, so nothing was touched. 'failed': the clear failed and
// the app keeps running as it is.
export type UpdateResult = 'reloading' | 'unreachable' | 'failed';

const AUTO_UPDATE_ATTEMPTED_KEY = 'auto_update_attempted';

class UpdateService {
  private cachedVersion: string | null = null;
  private readonly VERSION_KEY = 'app_version';

  constructor() {
    // Load cached version from localStorage on startup
    this.loadCachedVersion();
  }

  private getBaseDomainUrl(): string {
    // Extract base domain from current location, removing any CDN path
    const { protocol, hostname, port } = window.location;
    const portSuffix = port ? `:${port}` : '';
    // Add current query parameter to prevent caching issues
    return `${protocol}//${hostname}${portSuffix}${BUILD_TIME_PATH}?v=${Date.now()}`;
  }

  private loadCachedVersion(): void {
    try {
      const cached = localStorage.getItem(this.VERSION_KEY);
      if (cached) {
        this.cachedVersion = cached;
      }
    } catch (error) {
      console.warn('Error loading cached version:', error);
    }
  }

  private saveCachedVersion(version: string): void {
    try {
      localStorage.setItem(this.VERSION_KEY, version);
      this.cachedVersion = version;
    } catch (error) {
      console.warn('Error saving version to localStorage:', error);
    }
  }

  async checkForUpdate(): Promise<boolean> {
    try {
      store.set(isCheckingUpdateAtom, true);

      // Fetch from base domain (server will return index.html by default)
      const response = await fetch(this.getBaseDomainUrl(), {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // Parse the build-time.json response
      const data = await response.json();
      const newVersion = data.version;
      if (!newVersion) {
        throw new Error('Invalid version data received from server.');
      }

      // Store the new version info
      store.set(updateVersionAtom, newVersion);

      // If we have no cached version, cache this one and return false (no update)
      if (this.cachedVersion === null) {
        this.saveCachedVersion(newVersion);
        store.set(hasUpdateAtom, false);
        return false;
      }

      // Check if there's an update by comparing versions
      const hasUpdate = this.cachedVersion !== newVersion;
      store.set(hasUpdateAtom, hasUpdate);

      if (hasUpdate) {
        console.log('Update available:', {
          currentVersion: this.cachedVersion.substring(0, 16) + '...',
          newVersion: newVersion.substring(0, 16) + '...',
        });
      }

      return hasUpdate;
    } catch (error) {
      console.error('Error checking for update:', error);
      store.set(hasUpdateAtom, false);
      return false;
    } finally {
      store.set(isCheckingUpdateAtom, false);
    }
  }

  // The general online status checks the API host, so it can be true while the app host
  // is down. The update clears the service-worker caches and reloads from the app host,
  // so it checks that host answers first; every caller gets that check.
  async updateToNewVersion(): Promise<UpdateResult> {
    if (!(await this.isAppHostReachable())) {
      return 'unreachable';
    }

    const newVersion = store.get(updateVersionAtom);

    // Check if we're on a page that might have Dexie database operations
    // If so, navigate to home first
    const currentPath = window.location.pathname;
    const isDatabasePage =
      currentPath.includes('/inspection') ||
      currentPath.includes('/form') ||
      currentPath.includes('/bucket');

    const reload = () => {
      // Delay reload slightly to give SW time to settle
      setTimeout(() => {
        if (isDatabasePage) {
          // If we navigated away, ensure we go to home
          window.location.href = '/';
          return;
        }
        window.location.reload();
      }, 100);
    };

    try {
      await this.invalidateSWAndClearCache();
    } catch (error) {
      console.error('Error during update process:', error);
      // The service workers are unregistered before the caches are deleted. If that
      // part worked, the app is running with no service worker and would not start
      // offline, so reload while the host is up to register one again.
      if (await this.serviceWorkersUnregistered()) {
        reload();
        return 'reloading';
      }
      return 'failed';
    }

    // Acknowledge the version only once the clear has worked: recorded up front, a
    // failed clear left the update marked as applied, so it was never offered again
    // (#11624).
    if (newVersion) {
      this.saveCachedVersion(newVersion);
    }
    store.set(hasUpdateAtom, false);
    reload();
    return 'reloading';
  }

  // The login screen runs a pending update by itself, at most once per version per
  // session, so a clear that keeps failing does not hold up every launch with an
  // "Updating" screen. sessionStorage survives the update's own reload.
  wasAutoUpdateAttempted(version: string | null): boolean {
    try {
      return (
        version !== null &&
        sessionStorage.getItem(AUTO_UPDATE_ATTEMPTED_KEY) === version
      );
    } catch {
      return false;
    }
  }

  markAutoUpdateAttempted(version: string): void {
    try {
      sessionStorage.setItem(AUTO_UPDATE_ATTEMPTED_KEY, version);
    } catch (error) {
      console.warn('Error recording the automatic update attempt:', error);
    }
  }

  private async serviceWorkersUnregistered(): Promise<boolean> {
    try {
      return (
        'serviceWorker' in navigator &&
        (await navigator.serviceWorker.getRegistrations()).length === 0
      );
    } catch {
      return false;
    }
  }

  // The shared probe of the app host (HEAD /build-time.json).
  async isAppHostReachable(): Promise<boolean> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    try {
      return await probeAppHost(controller.signal);
    } catch (error) {
      console.warn('App host check failed:', error);
      return false;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async invalidateSWAndClearCache(): Promise<void> {
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((reg) => reg.unregister()));
      }

      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames
            .filter((cacheName) => cacheName !== 'photos')
            .map((name) => caches.delete(name)),
        );
      }
      // Removed setTimeout for reload; caller should handle reload after awaiting this.
    } catch (err) {
      console.error('Error invalidating SW and clearing cache:', err);
      throw err; // Re-throw error to allow the caller to handle it.
    }
  }

  initializeCachedVersion(): void {
    // Check for updates on app start (uses cached version from localStorage)
    this.checkForUpdate().catch((error) => {
      console.error('Error initializing update service:', error);
    });
  }
}

export const updateService = new UpdateService();
